import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import Investor from '../../models/Investor';
import { StyledButton } from '../styles/StyledButtons';
import {
  AdvisingCallout,
  EOIInvestmentInput,
  hasAdvisingFor,
  SuggestionCommentField,
} from './EOIFormControls';
import { EOIIntroText, EOITitle, EOITypography } from './EOISettingsContext';
import { SuggestedEOISummary } from './SuggestedEOISummary';

export const EOISectionObserverUpdatePanel: React.FC<{
  advisingFor?: string | null;
  investmentInput: string | undefined;
  validationError: string | undefined;
  validInvestment: number | undefined;
  investmentMessage: string;
  suggestionComment: string;
  suggesting: boolean;
  submitting: boolean;
  setInvestmentInput: (value: string | undefined) => void;
  setSuggestionComment: (value: string) => void;
  resetSuggestionEdit: () => void;
  handleSubmit: () => void;
}> = ({
  advisingFor,
  investmentInput,
  validationError,
  validInvestment,
  investmentMessage,
  suggestionComment,
  suggesting,
  submitting,
  setInvestmentInput,
  setSuggestionComment,
  resetSuggestionEdit,
  handleSubmit,
}) => (
  <>
    <EOITitle>Suggest an Update</EOITitle>
    <EOIIntroText />
    {hasAdvisingFor(advisingFor) ? (
      <AdvisingCallout advisingFor={advisingFor} variant="suggest-update" />
    ) : null}
    <EOITypography>{investmentMessage}</EOITypography>
    <EOIInvestmentInput
      investmentInput={investmentInput}
      validationError={validationError}
      editable
      onChange={setInvestmentInput}
    />
    <SuggestionCommentField value={suggestionComment} onChange={setSuggestionComment} />
    <Box className="eoiButtons two-buttons" mt={2} display="flex" gap={2}>
      <StyledButton disabled={!validInvestment || suggesting || submitting} onClick={handleSubmit}>
        {(suggesting || submitting) && (
          <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
        )}
        Suggest an Update
      </StyledButton>
      <StyledButton buttonVariant="inactiveOrCancel" onClick={resetSuggestionEdit}>
        Cancel
      </StyledButton>
    </Box>
  </>
);

export const EOISectionObserverReviseSuggestionPanel: React.FC<{
  investor: Investor;
  advisingFor?: string | null;
  investmentInput: string | undefined;
  validationError: string | undefined;
  validInvestment: number | undefined;
  investmentMessage: string;
  suggestionComment: string;
  suggesting: boolean;
  submitting: boolean;
  setInvestmentInput: (value: string | undefined) => void;
  setSuggestionComment: (value: string) => void;
  resetSuggestionEdit: () => void;
  handleSubmit: () => void;
}> = ({
  investor,
  advisingFor,
  investmentInput,
  validationError,
  validInvestment,
  investmentMessage,
  suggestionComment,
  suggesting,
  submitting,
  setInvestmentInput,
  setSuggestionComment,
  resetSuggestionEdit,
  handleSubmit,
}) => (
  <>
    <EOITitle>Suggest an Update</EOITitle>
    <EOIIntroText />
    {hasAdvisingFor(advisingFor) ? (
      <AdvisingCallout advisingFor={advisingFor} variant="suggest-update" />
    ) : null}
    <SuggestedEOISummary investor={investor} perspective="observer" isCurrentAdvisorSuggestion />
    <Box sx={{ mt: 2 }}>
      <EOITypography>{investmentMessage}</EOITypography>
    </Box>
    <EOIInvestmentInput
      investmentInput={investmentInput}
      validationError={validationError}
      editable
      onChange={setInvestmentInput}
    />
    <SuggestionCommentField value={suggestionComment} onChange={setSuggestionComment} />
    <Box className="eoiButtons two-buttons" mt={2} display="flex" gap={2}>
      <StyledButton disabled={!validInvestment || suggesting || submitting} onClick={handleSubmit}>
        {(suggesting || submitting) && (
          <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
        )}
        Suggest an Update
      </StyledButton>
      <StyledButton buttonVariant="inactiveOrCancel" onClick={resetSuggestionEdit}>
        Cancel
      </StyledButton>
    </Box>
  </>
);

export const EOISectionObserverSuggestionViewPanel: React.FC<{
  investor: Investor;
  isCurrentAdvisorSuggestion: boolean;
  suggesting: boolean;
  withdrawing: boolean;
  setUpdateMode: (value: boolean) => void;
  handleWithdrawSuggestedEOI: () => void;
}> = ({
  investor,
  isCurrentAdvisorSuggestion,
  suggesting,
  withdrawing,
  setUpdateMode,
  handleWithdrawSuggestedEOI,
}) => (
  <>
    <EOITitle />
    <EOIIntroText />
    <SuggestedEOISummary
      investor={investor}
      perspective="observer"
      isCurrentAdvisorSuggestion={isCurrentAdvisorSuggestion}
    />
    {isCurrentAdvisorSuggestion && (
      <Box sx={{ mt: 2 }} display="flex" gap={2}>
        <StyledButton onClick={() => setUpdateMode(true)} disabled={suggesting || withdrawing}>
          {suggesting && <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />}
          Update Suggestion
        </StyledButton>
        <StyledButton
          buttonVariant="inactiveOrCancel"
          className="destructive"
          onClick={handleWithdrawSuggestedEOI}
          disabled={suggesting || withdrawing}
        >
          {withdrawing && <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />}
          Withdraw Suggestion
        </StyledButton>
      </Box>
    )}
  </>
);
